/**
 * Zodiac ring — the drawn frame that replaced image generation for the orb.
 *
 * Dev-only preview module. Renders an SVG astrolabe ring whose sky rotates to
 * the sun's real ecliptic longitude. Approved direction: FINE weight (2026-08-21).
 *
 * Load-bearing decisions, all operator rulings:
 *   - The APPARATUS is fixed (rims, graduation band, crosshair, index); the SKY
 *     rotates beneath it (zodiac band, spectral arcs, guilloche). A real
 *     instrument behaves this way, and it is what makes the rotation legible.
 *   - One degree per day. No settle-on-load, no spin-up: rotation is a pure
 *     function of the date with no animation attached.
 *   - The ring NEVER carries system state — it is a calendar. State lives in the
 *     core only.
 *   - GLYPHS ARE PLACEHOLDERS. The operator is hand-drawing the twelve marks;
 *     swap `GLYPH` for paths and the placement/sizing/counter-rotation still hold.
 */
const NS = 'http://www.w3.org/2000/svg';
// Gold has to darken for the cream ground - the design system's own light
// derivation rule ("gold darkens so catch-lights read on cream"). #C9A84C on
// #F4EEE3 is barely above 1.8:1, which is why the linework and the glyphs both
// vanished in light mode. Set per-render in buildRing.
const GOLD_DARK = '#C9A84C';
const GOLD_LIGHT = '#8A6D2F';
let GOLD = GOLD_DARK;
const ORANGE = '#FF6B00';
const AMBER = '#D4870A';   // day marker - reads on both grounds

export const SIGNS = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];

// U+FE0E is VARIATION SELECTOR-15: forces text presentation. Without it the
// browser picks an emoji font and the signs render as purple pictographs.
const GLYPH = ['♈︎', '♉︎', '♊︎', '♋︎',
  '♌︎', '♍︎', '♎︎', '♏︎',
  '♐︎', '♑︎', '♒︎', '♓︎'];

/**
 * Apparent solar ecliptic longitude in degrees (0 = Aries).
 * Mean longitude plus the equation of the centre. A linear day-of-year
 * approximation is NOT sufficient — it ignores orbital eccentricity, runs ~3.5°
 * fast in August, and lands the sun in the wrong sign at a cusp.
 */
export function solarLongitude(date) {
  const n = (date - Date.UTC(2000, 0, 1, 12, 0, 0)) / 86400000;
  const M = ((357.5291 + 0.98560028 * n) % 360) * Math.PI / 180;
  const L = (280.459 + 0.98564736 * n) % 360;
  const C = 1.9148 * Math.sin(M) + 0.0200 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M);
  return ((L + C) % 360 + 360) % 360;
}

export const signOf = (lon) => SIGNS[Math.floor(lon / 30) % 12];

/**
 * Solar hour angle as a ring bearing, in degrees clockwise from 12 o'clock.
 * Solar noon at the top, midnight at the bottom — the daily companion to the
 * annual sky. Host-local time, deliberately: an ambient day-marker tracks the
 * sky where the device actually is, and since 2026-09-02 the greeting clock
 * (src/lib/heroClock.js) is host-local too, so the two agree everywhere.
 *
 * The ring now runs at TWO rates: the sky turns once a year, this marker once a
 * day. Both are ambient truth, so neither breaks the rule that the ring is a
 * calendar and never carries system state.
 */
export function dayAngle(date) {
  const mins = date.getHours() * 60 + date.getMinutes();
  return ((mins / 1440) * 360 + 180) % 360;   // +180 puts midnight at the bottom
}

const el = (n, a = {}) => {
  const e = document.createElementNS(NS, n);
  for (const k in a) e.setAttribute(k, a[k]);
  return e;
};
const polar = (r, deg) => {
  const a = (deg - 90) * Math.PI / 180;
  return [200 + Math.cos(a) * r, 200 + Math.sin(a) * r];
};
const seg = (r1, r2, deg, stroke, w, op) => {
  const [x1, y1] = polar(r1, deg); const [x2, y2] = polar(r2, deg);
  return el('line', { x1, y1, x2, y2, stroke, 'stroke-width': w, 'stroke-opacity': op });
};
const rim = (r, w, op, stroke = GOLD) =>
  el('circle', { cx: 200, cy: 200, r, fill: 'none', stroke, 'stroke-width': w, 'stroke-opacity': op });

// Spectral hue distributed around the circumference. This is the "spectrum"
// that survived triage: colour living IN the structure, never washed over the UI.
//
// Theme-aware since 2026-08-21. The dark values are tuned for a black ground and
// go highlighter-bright on cream. Light mode LIFTS the alpha (the operator likes
// the pastel and wants it to carry more) while only MODERATELY darkening the hue
// - pushing it too dark clashes against the cream rather than sitting on it.
let LIGHT = false;
const SECTOR_ALPHA = { dark: 0.11, light: 0.28 };   // 0.28 chosen 2026-08-21
let sectorAlpha = SECTOR_ALPHA.dark;

/** The ring's hue mapping, exported so LatticeCore can sit on the SAME wheel. */
export const wheelHue = (deg) => (deg + 20) % 360;

const hueAt = (deg, s, l, a) => LIGHT
  ? `hsla(${wheelHue(deg)},${Math.round(s * 0.78)}%,${Math.round(l * 0.80)}%,${a})`
  : `hsla(${wheelHue(deg)},${s}%,${l}%,${a})`;

function graduations(g, r1, r2, step, majorEvery, w, op, spectral) {
  for (let d = 0; d < 360; d += step) {
    const major = d % majorEvery === 0;
    const rr = major ? r2 : r1 + (r2 - r1) * 0.45;
    g.appendChild(seg(r1, rr, d,
      spectral ? hueAt(d, 55, 62, major ? 0.85 : 0.45) : GOLD,
      major ? w * 1.6 : w, major ? op : op * 0.6));
  }
}

function sectorPath(r1, r2, a0, a1) {
  const p1 = polar(r2, a0); const p2 = polar(r2, a1);
  const p3 = polar(r1, a1); const p4 = polar(r1, a0);
  return `M${p1}A${r2},${r2} 0 0 1 ${p2}L${p3}A${r1},${r1} 0 0 0 ${p4}Z`;
}

function guilloche(g, rIn, rOut, count) {
  for (let i = 0; i < count; i++) {
    const d = i * (360 / count);
    g.appendChild(seg(rIn, rOut, d, hueAt(d * 3, 60, 58, 0.3), 0.6, 1));
  }
}

/**
 * Cardinal axis marks. These used to run from the CENTRE outward, which put
 * their arrowheads on top of the orb - marks drawn over the subject rather than
 * apparatus framing it. They now start outside `coreR` and point inward at the
 * core, so the crosshair sits behind the core instead of crossing it.
 */
function crosshair(g, rInner, rOuter) {
  for (let i = 0; i < 4; i++) {
    const d = i * 90;
    g.appendChild(seg(rInner, rOuter, d, GOLD, 0.7, 0.3));
    const t = polar(rInner, d);
    const b1 = polar(rInner + 9, d - 1.7); const b2 = polar(rInner + 9, d + 1.7);
    g.appendChild(el('path', { d: `M${t}L${b1}L${b2}Z`, fill: GOLD, 'fill-opacity': 0.7 }));
  }
}

/**
 * The day marker: a small sun riding its own track, plus a fine radial tick.
 * Deliberately amber rather than the index's orange — one is a fixed reference,
 * the other moves, and they must not read as the same kind of thing.
 */
function dayMarker(r, deg) {
  const g = el('g', { 'data-day-hand': '', transform: `rotate(${deg} 200 200)` });
  g.appendChild(el('line', {
    x1: 200, y1: 200 - (r - 7), x2: 200, y2: 200 - (r + 7),
    stroke: AMBER, 'stroke-width': 1.4, 'stroke-opacity': 0.9,
  }));
  g.appendChild(el('circle', {
    cx: 200, cy: 200 - r, r: 4.2, fill: AMBER, 'fill-opacity': 0.95,
  }));
  g.appendChild(el('circle', {
    cx: 200, cy: 200 - r, r: 7.5, fill: 'none',
    stroke: AMBER, 'stroke-width': 0.7, 'stroke-opacity': 0.45,
  }));
  return g;
}

/**
 * Ring detail tier for a rendered size in CSS px. Exported so the sizing rule
 * is testable: the fluid hero token (spec §4.2) lands phones in `mid`.
 */
export function lodTier(size) {
  return size >= 280 ? 'full' : size >= 150 ? 'mid' : 'min';
}

/**
 * Build the ring. `coreR` is the clear radius left for the core — nothing is
 * drawn inside it, so the real orb shows through.
 */
export function buildRing(size, lon, coreR = 104, theme = 'dark', opts = {}) {
  LIGHT = theme === 'light';
  GOLD = LIGHT ? GOLD_LIGHT : GOLD_DARK;
  sectorAlpha = opts.sectorAlpha != null
    ? opts.sectorAlpha
    : (LIGHT ? SECTOR_ALPHA.light : SECTOR_ALPHA.dark);
  const tier = lodTier(size);
  const svg = el('svg', {
    viewBox: '0 0 400 400', width: size, height: size,
    class: 'zring fine', role: 'img',
    'aria-label': `Zodiac ring, sun in ${signOf(lon)}`,
  });

  // ── SKY: turns with the year ────────────────────────────────────
  const sky = el('g', { transform: `rotate(${-lon} 200 200)` });
  if (tier === 'full') {
    graduations(sky, 168, 182, 1, 10, 0.5, 0.5, true);
    sky.appendChild(rim(168, 0.7, 0.4));
  }
  if (tier !== 'min') {
    sky.appendChild(rim(166, 0.8, 0.5));
    sky.appendChild(rim(140, 0.8, 0.5));
    for (let i = 0; i < 12; i++) {
      const d = i * 30;
      sky.appendChild(seg(140, 166, d, GOLD, 0.9, 0.75));
      sky.appendChild(el('path', {
        d: sectorPath(140, 166, d, d + 30),
        fill: hueAt(d + 15, 50, 50, sectorAlpha), stroke: 'none',
      }));
    }
    if (tier === 'full') graduations(sky, 140, 147, 2.5, 30, 0.4, 0.35, false);
    for (let q = 0; q < 12; q++) {
      const ga = q * 30 + 15;
      const [gx, gy] = polar(153, ga);
      const tx = el('text', {
        x: gx, y: gy, class: 'glyph', 'font-size': 15,
        transform: `rotate(${lon} ${gx} ${gy})`,
      });
      tx.textContent = GLYPH[q];
      sky.appendChild(tx);
    }
  } else {
    for (let k = 0; k < 4; k++) sky.appendChild(seg(132, 150, k * 90, GOLD, 1.6, 0.8));
    sky.appendChild(rim(150, 1.2, 0.7));
  }
  if (tier === 'full') {
    [[132, 2.6, 0.75], [127, 1.6, 0.55], [122, 3.2, 0.5], [117, 1.1, 0.4]]
      .forEach(([r, w, a], n) => sky.appendChild(
        el('circle', {
          cx: 200, cy: 200, r, fill: 'none',
          // the harsh one: 78% saturation reads as neon on black and as
          // highlighter on cream, so light drops saturation hard and alpha some
          stroke: hueAt(280 + n * 26, LIGHT ? 52 : 78, LIGHT ? 46 : 58,
            LIGHT ? a * 0.72 : a),
          'stroke-width': w,
        })));
    guilloche(sky, coreR + 2, coreR + 8, 120);
  }
  svg.appendChild(sky);

  // ── APPARATUS: fixed ────────────────────────────────────────────
  const frame = el('g');
  if (tier !== 'min') {
    frame.appendChild(rim(190, 0.9, 0.5));
    frame.appendChild(rim(186, 0.5, 0.35));
    if (tier === 'full') graduations(frame, 183, 190, 2, 30, 0.4, 0.4, false);
    frame.appendChild(rim(172, 0.7, 0.4));
    // sits clear of the guilloche band (coreR+2..+8) so the two do not tangle
    crosshair(frame, coreR + 11, coreR + 31);
  } else {
    frame.appendChild(rim(158, 1.4, 0.8));
  }
  // Day marker on its own track, clear of the crosshair (coreR+11..+31) and of
  // the zodiac band (140+). Drawn in the fixed layer because it does not follow
  // the annual rotation - it keeps its own time.
  if (tier !== 'min') frame.appendChild(dayMarker(coreR + 32, dayAngle(new Date())));

  // Index at twelve o'clock, pointing INWARD. Extending it outward from the rim
  // puts its base at a negative y and clips it off the viewBox.
  const ri = tier === 'min' ? 158 : 190;
  const baseY = 200 - ri + 2;
  frame.appendChild(el('polygon', {
    points: `200,${baseY + 14} 194,${baseY} 206,${baseY}`, fill: ORANGE,
  }));
  svg.appendChild(frame);
  return svg;
}
